import { useMemo } from "react";
import type { MapInteractionAdapter, UseMapInteractionAdapter } from "./types";

const IDLE_MAP_INTERACTION: MapInteractionAdapter = {
  isPickZoneActive: false,
  overlay: null,
};

export const useMapInteractionAdapter: UseMapInteractionAdapter = ({
  interactionMode,
  creationModifier,
  isRouteDrawingActive,
  isPickingCalibration,
  routeCreationOverlay,
  pathEditingOverlay,
  calibrationOverlay,
  onPickPositionFromMap,
  onRouteMapClick,
  onRouteMapMouseMove,
  onPathEditingMapMouseMove,
  onSelectionAreasMapClick,
  onSelectionAreasMapMouseMove,
  onRoomsMapClick,
  onRoomsMapMouseMove,
  onCalibrationMapClick,
  onCalibrationMapMouseMove,
}) => {
  const mapInteraction = useMemo<MapInteractionAdapter>(() => {
    switch (interactionMode.kind) {
      case "idle": {
        return IDLE_MAP_INTERACTION;
      }
      case "creating-element": {
        return {
          isPickZoneActive: creationModifier !== "none",
          onMapClick: onPickPositionFromMap,
          overlay: null,
        };
      }
      case "editing-element": {
        return {
          isPickZoneActive: true,
          onMapClick: onPickPositionFromMap,
          overlay: null,
        };
      }
      case "drawing-route": {
        return {
          isPickZoneActive: isRouteDrawingActive,
          onMapClick: onRouteMapClick,
          onMapMouseMove: onRouteMapMouseMove,
          overlay: routeCreationOverlay,
        };
      }
      case "editing-route": {
        return {
          isPickZoneActive: true,
          onMapMouseMove: onRouteMapMouseMove,
          overlay: routeCreationOverlay,
        };
      }
      case "editing-path": {
        return {
          isPickZoneActive: true,
          onMapMouseMove: onPathEditingMapMouseMove,
          overlay: pathEditingOverlay,
        };
      }
      case "selection-areas": {
        return {
          isPickZoneActive: true,
          onMapClick: onSelectionAreasMapClick,
          onMapMouseMove: onSelectionAreasMapMouseMove,
          overlay: null,
        };
      }
      case "drawing-rooms":
      case "editing-room": {
        return {
          isPickZoneActive: true,
          onMapClick: onRoomsMapClick,
          onMapMouseMove: onRoomsMapMouseMove,
          overlay: null,
        };
      }
      case "merging-rooms": {
        return {
          isPickZoneActive: true,
          onMapClick: onRoomsMapClick,
          overlay: null,
        };
      }
      case "calibrating": {
        return {
          isPickZoneActive: isPickingCalibration,
          onMapClick: onCalibrationMapClick,
          onMapMouseMove: onCalibrationMapMouseMove,
          overlay: calibrationOverlay,
        };
      }
    }
  }, [
    interactionMode,
    creationModifier,
    isRouteDrawingActive,
    isPickingCalibration,
    routeCreationOverlay,
    pathEditingOverlay,
    calibrationOverlay,
    onPickPositionFromMap,
    onRouteMapClick,
    onRouteMapMouseMove,
    onPathEditingMapMouseMove,
    onSelectionAreasMapClick,
    onSelectionAreasMapMouseMove,
    onRoomsMapClick,
    onRoomsMapMouseMove,
    onCalibrationMapClick,
    onCalibrationMapMouseMove,
  ]);

  return mapInteraction;
};
